// MoveFieldForm.tsx

import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { FieldWithId, GroupWithId, SectionWithId } from './MetadataInterfaces';
import { PropertyTemplatesWithIds } from './MetadataInterfaces';

interface MoveFieldFormProps {
  field: FieldWithId;
  currentPropertyType: string;
  currentGroup: GroupWithId;
  currentSection: SectionWithId;
  propertyTemplates: PropertyTemplatesWithIds;
  onSave: (
    field: FieldWithId,
    destinationPropertyType: string,
    destinationGroupId: string,
    destinationSectionId: string,
    isCopy: boolean,
  ) => void;
  onCancel: () => void;
}

const MoveFieldForm: React.FC<MoveFieldFormProps> = ({
  field,
  currentPropertyType,
  currentGroup,
  currentSection,
  propertyTemplates,
  onSave,
  onCancel,
}) => {
  const [selectedPropertyType, setSelectedPropertyType] =
    useState(currentPropertyType);
  const [selectedGroupId, setSelectedGroupId] = useState(currentGroup.id);
  const [selectedSectionId, setSelectedSectionId] = useState(currentSection.id);
  const [isCopy, setIsCopy] = useState(false);

  const propertyTypes = Object.keys(propertyTemplates);
  const groups = propertyTemplates[selectedPropertyType].groups;
  const sections = groups.find((g) => g.id === selectedGroupId)?.sections || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    onSave(
      field,
      selectedPropertyType,
      selectedGroupId,
      selectedSectionId,
      isCopy,
    );
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <FormControl fullWidth margin="normal">
        <InputLabel>Property Type</InputLabel>
        <Select
          value={selectedPropertyType}
          onChange={(e) => setSelectedPropertyType(e.target.value)}
        >
          {propertyTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel>Group</InputLabel>
        <Select
          value={selectedGroupId}
          onChange={(e) => {
            setSelectedGroupId(e.target.value);
            setSelectedSectionId('');
          }}
        >
          {groups.map((group) => (
            <MenuItem key={group.id} value={group.id}>
              {group.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel>Section</InputLabel>
        <Select
          value={selectedSectionId}
          onChange={(e) => setSelectedSectionId(e.target.value)}
        >
          {sections.map((section) => (
            <MenuItem key={section.id} value={section.id}>
              {section.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={isCopy}
            onChange={(e) => setIsCopy(e.target.checked)}
          />
        }
        label="Copy field instead of moving"
      />
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {isCopy ? 'Copy Field' : 'Move Field'}
        </Button>
      </Box>
    </Box>
  );
};

export default MoveFieldForm;
